import type { PromoValidationResponse, StripeProduct } from "./api";

/**
 * Helpers for reading Stripe price objects and computing discounted totals.
 *
 * A Stripe `price` is delivered as an opaque dict (`{ [key]: unknown }`), so we
 * read fields defensively. All monetary amounts are in the currency's minor
 * unit (cents), matching Stripe and the backend's `get_subscription_data`.
 */

export type Coupon = PromoValidationResponse["coupon"];

export interface PriceInfo {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function readPrice(product: StripeProduct): PriceInfo {
  const price = product.price as Record<string, unknown>;
  const recurring = (price.recurring as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(price.id) ?? "",
    unitAmount: asNumber(price.unit_amount),
    currency: asString(price.currency) ?? "cad",
    interval: asString(recurring.interval),
    intervalCount: asNumber(recurring.interval_count),
  };
}

/**
 * Apply a coupon to a base amount (in cents), mirroring the backend's
 * `get_subscription_data`: percentage first, then fixed amount, clamped at 0.
 */
export function applyDiscount(baseCents: number, coupon: Coupon | null): number {
  if (!coupon) return baseCents;
  let total = baseCents;
  if (coupon.percent_off) {
    total -= total * (coupon.percent_off / 100);
  }
  if (coupon.amount_off) {
    total -= coupon.amount_off;
  }
  return Math.max(0, Math.round(total));
}

/** Format a minor-unit (cents) amount as localized currency. */
export function formatCents(
  cents: number | null | undefined,
  currency: string,
  language: string,
): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Human-readable "/ month" style suffix for a recurring price. */
export function intervalLabel(
  price: PriceInfo,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!price.interval) return "";
  const count = price.intervalCount ?? 1;
  const unit = t(`billing.interval.${price.interval}`, {
    count,
    defaultValue: price.interval,
  });
  return count > 1 ? `/ ${count} ${unit}` : `/ ${unit}`;
}
